/**
 * IdleScreen Component
 *
 * Initial state: Invitation to join the collective experience
 */

import React, { useState } from 'react';
import COLORS from '../constants/colors';

// Play icon SVG
const PlayIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="5 3 19 12 5 21 5 3"></polygon>
  </svg>
);

// User icon SVG
const UserIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

// Users icon SVG
const UsersIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
    <circle cx="9" cy="7" r="4"></circle>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
  </svg>
);

// TV/Monitor icon SVG for livestream
const TvIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="15" rx="2" ry="2"></rect>
    <polyline points="17 2 12 7 7 2"></polyline>
  </svg>
);

const IdleScreen = ({ onStart, visualTaps = [], triggerVisualTap }) => {
  const [mode, setMode] = useState(null); // null, 'single', 'multi'
  const [sessionId, setSessionId] = useState('');

  const handleModeSelect = (selectedMode) => {
    setMode(selectedMode);
  };

  const handleStartGame = () => {
    if (mode === 'single') {
      onStart({ mode: 'single', gameId: null });
    } else if (mode === 'multi' && sessionId.trim()) {
      onStart({ mode: 'multi', gameId: sessionId.trim().toUpperCase() });
    }
  };

  const handleBack = () => {
    setMode(null);
    setSessionId('');
  };
  return (
    <div className="w-full max-w-full h-screen bg-black text-cyan-400 font-mono text-center overflow-y-auto overflow-x-hidden" style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}>
      <div className="flex flex-col items-center justify-start md:justify-center min-h-full pt-2 px-3 pb-3 md:p-6 relative">
      {/* Visual Taps Overlay */}
      {visualTaps && visualTaps.length > 0 && (
        <div className="fixed inset-0 z-50 pointer-events-none overflow-hidden">
          {visualTaps.map(tap => (
            <div
              key={tap.id}
              className="absolute w-12 h-12 rounded-full border-2 border-pink-500 bg-pink-500/30 shadow-[0_0_15px_rgba(236,72,153,0.8),inset_0_0_10px_rgba(236,72,153,0.5)] animate-ping"
              style={{
                left: `${tap.x}%`,
                top: `${tap.y}%`,
                animationDuration: '0.6s'
              }}
            />
          ))}
        </div>
      )}

      {/* Promotional Banner */}
      <a
        href="https://claimfreedom.org"
        target="_blank"
        rel="noopener noreferrer"
        className="group relative mt-2 mb-4 md:mb-6 px-3 py-2 md:px-4 md:py-3 max-w-2xl w-full bg-gradient-to-r from-amber-900/40 via-cyan-900/40 to-amber-900/40 border border-cyan-500/50 hover:border-amber-400 rounded-lg transition-all duration-300 overflow-hidden shadow-[0_0_20px_rgba(34,211,238,0.2)] hover:shadow-[0_0_30px_rgba(251,191,36,0.4)] relative z-10"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-amber-400/10 via-cyan-400/10 to-amber-400/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
        <div className="relative flex flex-col gap-1 text-center">
          <div className="text-xs tracking-widest text-red-400/90 font-bold uppercase">That Voice In Your Head That Never Stops?</div>
          <div className="text-sm sm:text-base text-cyan-100/90 font-semibold">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-amber-300">You Are Not That Voice.</span> Learn How to Break Free.
          </div>
          <div className="text-xs text-amber-400/80 font-semibold">
            📚 Free Books & Ancient Practices
          </div>
          <div className="text-xs text-cyan-400 group-hover:text-amber-300 font-bold transition-colors mt-0.5">
            <span className="underline">Click Here</span> → ClaimFreedom.org
          </div>
        </div>
      </a>

      {/* Title with gradient */}
      <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold mb-2 md:mb-4 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-600 relative z-10 animate-pulse-glow px-2">
        OPERATION: CRITICAL MASS
      </h1>

      {/* Description */}
      <p className="text-base sm:text-lg md:text-xl mb-3 md:mb-4 max-w-md text-cyan-100/90 relative z-10 px-4 font-semibold">
        Join other players in a collective experience of synchronization and coherence.
      </p>

      {/* How to Play - Only shown after mode selection */}
      {mode && (
        <div className="mb-4 md:mb-8 max-w-md relative z-10 px-4 bg-cyan-950/30 border border-cyan-700/50 rounded-lg py-3 shadow-[0_0_20px_rgba(34,211,238,0.2)]">
          <div className="text-cyan-300 text-sm sm:text-base font-bold mb-2 tracking-wide">
            🎮 HOW TO PLAY
          </div>
          <div className="flex flex-col gap-2 text-left text-sm sm:text-base text-cyan-100/80">
            <div className="flex items-start gap-2">
              <span className="text-amber-400 font-bold">1.</span>
              <span><strong className="text-cyan-300">Tap in rhythm</strong> - Press the glowing button when it lights up</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-amber-400 font-bold">2.</span>
              <span><strong className="text-red-300">Clear intrusive thoughts</strong> - Tap red bubbles to dismiss them</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-amber-400 font-bold">3.</span>
              <span><strong className="text-amber-300">Achieve breakthrough</strong> - Reach 100% coherence together</span>
            </div>
          </div>
        </div>
      )}

      {/* Mode Selection */}
      {!mode ? (
        <div className="flex flex-col gap-4 relative z-20">
          {/* Single Player Button */}
          <button
            onClick={() => handleModeSelect('single')}
            className="group relative px-4 py-3 md:px-8 md:py-4 bg-cyan-900/30 border-2 border-cyan-500 hover:bg-cyan-500/20 transition-all rounded-xl overflow-hidden shadow-[0_0_20px_rgba(34,211,238,0.3)] hover:shadow-[0_0_30px_rgba(34,211,238,0.5)] active:scale-95 w-full max-w-sm select-none"
          >
            <div className="absolute inset-0 bg-cyan-400/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
            <span className="relative flex items-center justify-center gap-2 md:gap-3 text-lg md:text-xl font-bold tracking-widest text-cyan-400">
              <UserIcon size={24} /> SINGLE PLAYER
            </span>
            <span className="relative block text-xs text-cyan-300/60 mt-1">Play offline</span>
          </button>

          {/* Multiplayer Button */}
          <button
            onClick={() => handleModeSelect('multi')}
            className="group relative px-4 py-3 md:px-8 md:py-4 bg-amber-900/30 border-2 border-amber-500 hover:bg-amber-500/20 transition-all rounded-xl overflow-hidden shadow-[0_0_20px_rgba(251,191,36,0.3)] hover:shadow-[0_0_30px_rgba(251,191,36,0.5)] active:scale-95 w-full max-w-sm select-none"
          >
            <div className="absolute inset-0 bg-amber-400/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
            <span className="relative flex items-center justify-center gap-2 md:gap-3 text-lg md:text-xl font-bold tracking-widest text-amber-400">
              <UsersIcon size={24} /> MULTIPLAYER
            </span>
            <span className="relative block text-xs text-amber-300/60 mt-1">Join collective game</span>
          </button>
        </div>
      ) : mode === 'single' ? (
        <div className="flex flex-col gap-4 relative z-20">
          {/* Single Player Confirmation */}
          <button
            onClick={handleStartGame}
            className="group relative px-4 py-3 md:px-8 md:py-4 bg-cyan-900/30 border-2 border-cyan-500 hover:bg-cyan-500/20 transition-all rounded-xl overflow-hidden shadow-[0_0_20px_rgba(34,211,238,0.3)] hover:shadow-[0_0_30px_rgba(34,211,238,0.5)] active:scale-95 w-full max-w-sm select-none"
          >
            <div className="absolute inset-0 bg-cyan-400/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
            <span className="relative flex items-center justify-center gap-2 md:gap-3 text-lg md:text-xl font-bold tracking-widest text-cyan-400">
              <PlayIcon size={24} /> START GAME
            </span>
          </button>
          <button
            onClick={handleBack}
            className="text-cyan-400/60 hover:text-cyan-400 transition-colors text-sm select-none"
          >
            ← Back
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-4 relative z-20 w-full max-w-md">
          {/* Multiplayer Game ID Input */}
          <div className="flex flex-col gap-2">
            <label className="text-amber-400 text-sm font-bold tracking-wider">ENTER SESSION ID</label>
            <input
              type="text"
              value={sessionId}
              onChange={(e) => setSessionId(e.target.value)}
              placeholder="e.g., CRITICAL-MASS-2024"
              className="px-4 py-3 bg-gray-900/50 border-2 border-amber-500/50 rounded-lg text-amber-100 placeholder-gray-600 focus:outline-none focus:border-amber-400 focus:shadow-[0_0_20px_rgba(251,191,36,0.3)] transition-all"
              onKeyPress={(e) => e.key === 'Enter' && sessionId.trim() && handleStartGame()}
            />
            <p className="text-xs text-amber-300/70 text-left">
              Enter an existing session ID to join, or create a new session with any unique ID
            </p>
          </div>
          <button
            onClick={handleStartGame}
            disabled={!sessionId.trim()}
            className="group relative px-4 py-3 md:px-8 md:py-4 bg-amber-900/30 border-2 border-amber-500 hover:bg-amber-500/20 transition-all rounded-xl overflow-hidden shadow-[0_0_20px_rgba(251,191,36,0.3)] hover:shadow-[0_0_30px_rgba(251,191,36,0.5)] active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-amber-900/30 w-full select-none"
          >
            <div className="absolute inset-0 bg-amber-400/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
            <span className="relative flex items-center justify-center gap-2 md:gap-3 text-lg md:text-xl font-bold tracking-widest text-amber-400">
              <PlayIcon size={24} /> JOIN GAME
            </span>
          </button>

          {/* Livestream View Button */}
          <a
            href={sessionId.trim() ? `/?view=livestream&session=${encodeURIComponent(sessionId.trim().toUpperCase())}` : '#'}
            onClick={(e) => !sessionId.trim() && e.preventDefault()}
            className={`group relative px-4 py-3 md:px-8 md:py-4 bg-purple-900/30 border-2 border-purple-500 hover:bg-purple-500/20 transition-all rounded-xl overflow-hidden shadow-[0_0_20px_rgba(168,85,247,0.3)] hover:shadow-[0_0_30px_rgba(168,85,247,0.5)] active:scale-95 w-full select-none text-center ${!sessionId.trim() ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}
          >
            <div className="absolute inset-0 bg-purple-400/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
            <span className="relative flex items-center justify-center gap-2 md:gap-3 text-lg md:text-xl font-bold tracking-widest text-purple-400">
              <TvIcon size={24} /> LIVESTREAM VIEW
            </span>
            <span className="relative block text-xs text-purple-300/60 mt-1">Watch collective progress</span>
          </a>

          <button
            onClick={handleBack}
            className="text-amber-400/60 hover:text-amber-400 transition-colors text-sm select-none"
          >
            ← Back
          </button>
        </div>
      )}

      {/* Scientific Note */}
      <div className="mt-12 mb-16 text-cyan-200/50 text-xs text-center max-w-lg px-4">
        <p className="mb-2">
          This game uses bilateral stimulation based on EMDR research to promote
          hemispheric integration and present-moment awareness.
        </p>
        <p>
          Play with headphones for optimal experience.
        </p>
      </div>

      {/* Footer */}
      <div className="mt-4 mb-4 text-gray-600 text-xs">
        The Body is One
      </div>
      </div>
    </div>
  );
};

export default IdleScreen;
